<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  inheritAttrs: false,
});
</script>
<script setup lang="ts">
import { $userList } from '@/api/users'
import { uniqBy } from 'lodash'



interface IProps {
  modelValue?: any,
  label?: string,
  customEventRole?: any,
  assigneeCampaignId?: number | null | undefined,
  selectOptions?: any[] | null,
  isSelectAllVisible?: boolean,
  loadAll?: boolean,
  loading?: boolean,
  separateSearch?: boolean,
  /**
   * Optional array of user IDs that should be disabled for selection.
   * If null or undefined, no locations will be disabled.
   */
  disabledUsers?: number[] | null,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => {},
  label: 'User',
  customEventRole: () => {},
  assigneeCampaignId: null,
  selectOptions: null,
  isSelectAllVisible: false,
  loadAll: false,
  loading: false,
  separateSearch: false,
  disabledUsers: null
})

interface CustomEvents {
  (e: 'update:modelValue', val: any): void
  (e: 'update:focused', val: boolean): void
}

const emit = defineEmits<CustomEvents>()
const attrs = useAttrs()
const isMenuOpen = ref<boolean>(false)
const separateSearchActivatorRef = ref<{ $el?: HTMLElement } | null>(null)
const separateSearchMenuWidth = ref<number | undefined>()

interface User {
  id: number
  display_name?: string
  first_name?: string
  last_name?: string
  email?: string
}

const users = ref<User[]>([])
const usersCurrentPage = ref<number>(1)
const usersPerPage: number = 10
const usersSearchStr = ref<string>('')
const isNextUsersPageExist = ref<boolean>(false)
const usersLoading = ref<boolean>(false)
const isLoading = computed(() => usersLoading.value || props.loading)

const userOptions = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return users.value
  }
})

const localSearchStr = ref<string>('')

const getUserTitle = (user: User) => {
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()

  return user.display_name || fullName || user.email || ''
}

const filteredUsers = computed(() => {
  if (!localSearchStr.value) return userOptions.value
  const searchLower = localSearchStr.value.toLowerCase()
  return userOptions.value.filter((option: User) => {
    const displayName = option.display_name || `${option.first_name || ''} ${option.last_name || ''}`.trim()
    const email = option.email || ''
    return displayName.toLowerCase().includes(searchLower) || email.toLowerCase().includes(searchLower)
  })
})

const selectedValues = computed(() => Array.isArray(props.modelValue) ? props.modelValue : [])

const selectedSummary = computed(() => {
  if (!selectedValues.value.length)
    return ''

  return ' '
})

const isUserDisabled = (userId: number) => !!props.disabledUsers?.includes(userId)

const isUserSelected = (userId: number) =>
  selectedValues.value.some((selected: User) => selected.id === userId)

const allFilteredSelected = computed(() => {
  if (!filteredUsers.value.length) return false
  return filteredUsers.value.every((option: User) =>
    selectedValues.value.some((selected: User) => selected.id === option.id)
  )
})

const someFilteredSelected = computed(() => {
  if (!filteredUsers.value.length) return false
  const selectedCount = filteredUsers.value.filter((option: User) =>
    selectedValues.value.some((selected: User) => selected.id === option.id)
  ).length
  return selectedCount > 0 && selectedCount < filteredUsers.value.length
})

const toggleSelectAll = () => {
  if (allFilteredSelected.value || someFilteredSelected.value) {
    const newValue = selectedValues.value.filter((selected: User) =>
      !filteredUsers.value.some((option: User) => option.id === selected.id)
    )
    emit('update:modelValue', newValue)
  } else {
    const newValue = [...selectedValues.value]
    filteredUsers.value.forEach((option: User) => {
      if (!newValue.some((selected: User) => selected.id === option.id)) {
        newValue.push(option)
      }
    })
    emit('update:modelValue', newValue)
  }
}

const toggleUserSelection = (user: User) => {
  if (isUserDisabled(user.id))
    return

  if (isUserSelected(user.id)) {
    emit('update:modelValue', selectedValues.value.filter((selected: User) => selected.id !== user.id))

    return
  }

  emit('update:modelValue', [...selectedValues.value, user])
}

const clearSelection = () => {
  emit('update:modelValue', [])
}

const getUsers = async () => {
  usersLoading.value = true
  const filters = {
    // Get all users, regardless of role
    get_all: true,
    is_simple: true,  // Minimum number of fields('id', 'display_name')
    custom_event_role: props.customEventRole && props.customEventRole.id ? props.customEventRole.id : undefined,
    assignee_list_campaign_id: props.assigneeCampaignId ? props.assigneeCampaignId : undefined,
    search: usersSearchStr.value,
    per_page: props.loadAll ? -1 : usersPerPage,
    page: props.loadAll ? undefined : usersCurrentPage.value,
  }
  try {
    const res = await $userList(filters)

    // Append the new users to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    users.value = uniqBy([...users.value, ...res.data.results], 'id')

    isNextUsersPageExist.value = props.loadAll ? false : !!res.data.next
  }
  catch (e) {
    console.log(e)
  }
  finally {
    usersLoading.value = false
  }
}

const loadInitialUsers = async () => {
  if (usersLoading.value)
    return

  if (!users.value.length && !props.selectOptions) {
    users.value = []
    usersCurrentPage.value = 1
    await getUsers()
  }
}

const onFocusUserSelect = async (isFocused: boolean) => {
  emit('update:focused', isFocused)

  if (!isFocused)
    return

  await loadInitialUsers()
}

const syncSeparateSearchMenuWidth = async () => {
  await nextTick()

  const activatorElement = separateSearchActivatorRef.value?.$el
  const activatorWidth = activatorElement?.getBoundingClientRect().width

  separateSearchMenuWidth.value = activatorWidth ? Math.round(activatorWidth) : undefined
}

const onSeparateSearchActivatorFocused = async (isFocused: boolean) => {
  emit('update:focused', isFocused)

  if (!isFocused)
    return

  await syncSeparateSearchMenuWidth()
  await loadInitialUsers()
}

const onLoadMoreUsers = async () => {
  if (props.loadAll) return
  usersCurrentPage.value++ // Increment the current page to load the next page of users
  await getUsers()
}

const clearAndGetUsers = (searchStr: string) => {
  localSearchStr.value = searchStr
  users.value = []
  usersCurrentPage.value = 1 // Set current page => 1 to load initial first page of locations
  usersSearchStr.value = searchStr
  getUsers()
}

const normalizeSearchStr = (searchStr?: string | null) => searchStr ?? ''

const handleBackendUserSearch = useDebounceFn((searchStr: string) => {
  if (!props.selectOptions && !props.loadAll) {
    clearAndGetUsers(searchStr)
  }
}, 500)

const handleSeparateSearchInput = (searchStr?: string | null) => {
  const normalizedSearchStr = normalizeSearchStr(searchStr)

  localSearchStr.value = normalizedSearchStr
  if (!props.selectOptions && !props.loadAll) {
    handleBackendUserSearch(normalizedSearchStr)
  }
}

const handleAutocompleteSearch = useDebounceFn((searchStr?: string | null) => {
  const normalizedSearchStr = normalizeSearchStr(searchStr)

  localSearchStr.value = normalizedSearchStr
  if (!props.selectOptions && !props.loadAll) {
    clearAndGetUsers(normalizedSearchStr)
  }
}, 500)

watch(isMenuOpen, async isOpen => {
  if (!isOpen)
    return

  await syncSeparateSearchMenuWidth()
  await loadInitialUsers()
})

watch(() => props.customEventRole, () => {
  clearAndGetUsers('')
})

// On change AssigneeCampaignId - load users with new assigneeCampaignId prop
watch(() => props.assigneeCampaignId, () => {
  if (props.assigneeCampaignId) {
    emit('update:modelValue', null)
    users.value = []
    usersCurrentPage.value = 1 // Set current page => 1 to load initial first page of users
    usersSearchStr.value = ''
    getUsers()
  }
})
</script>

<template>
  <VMenu
    v-if="separateSearch"
    v-model="isMenuOpen"
    :close-on-content-click="false"
    :min-width="separateSearchMenuWidth"
    :max-width="separateSearchMenuWidth"
    location="bottom"
  >
    <template #activator="{ props: menuActivatorProps }">
      <VTextField
        ref="separateSearchActivatorRef"
        v-bind="{ ...attrs, ...menuActivatorProps }"
        :label="label"
        :model-value="selectedSummary"
        :append-inner-icon="isMenuOpen ? 'tabler-chevron-up' : 'tabler-chevron-down'"
        :loading="isLoading"
        density="compact"
        readonly
        clearable
        @click:clear.stop="clearSelection"
        @update:focused="onSeparateSearchActivatorFocused"
      >
        <template
          v-if="selectedValues.length"
          #prepend-inner
        >
          <div class="multiple-users-select__selection">
            <VMenu
              open-on-hover
              :open-on-click="false"
              :close-on-content-click="false"
              location="top"
            >
              <template #activator="{ props: chipMenuProps }">
                <VChip
                  v-bind="chipMenuProps"
                  class="me-2 multiple-users-select__chip"
                  :title="getUserTitle(selectedValues[0])"
                >
                  <span class="multiple-users-select__chip-text">
                    {{ getUserTitle(selectedValues[0]) }}
                  </span>
                </VChip>
              </template>

              <VCard class="multiple-users-select__chip-menu pa-2">
                {{ getUserTitle(selectedValues[0]) }}
              </VCard>
            </VMenu>
          </div>
          <span
            v-if="selectedValues.length > 1"
            class="text-grey text-caption align-self-center ms-2 multiple-users-select__more"
          >
            (+{{ selectedValues.length - 1 }} others)
          </span>
        </template>
      </VTextField>
    </template>

    <VCard
      class="multiple-users-select__menu"
      :style="{ width: separateSearchMenuWidth ? `${separateSearchMenuWidth}px` : undefined }"
    >
      <VCardText class="pb-2">
        <VTextField
          :model-value="localSearchStr"
          label="Search"
          density="compact"
          clearable
          autofocus
          hide-details
          @update:modelValue="handleSeparateSearchInput"
        />
      </VCardText>

      <VListItem
        v-if="isSelectAllVisible && filteredUsers?.length > 1"
        class="v-list-item--select-all"
        @click="toggleSelectAll"
      >
        <template #prepend>
          <VCheckboxBtn
            :model-value="allFilteredSelected"
            :indeterminate="someFilteredSelected"
          />
        </template>
        <VListItemTitle>
          {{ allFilteredSelected || someFilteredSelected ? 'Deselect' : 'Select' }} All
          ({{ filteredUsers.length }} found)
        </VListItemTitle>
      </VListItem>

      <VDivider v-if="isSelectAllVisible && filteredUsers?.length > 1" />

      <div
        v-if="!!isLoading && !filteredUsers.length"
        class="d-flex justify-center align-center multiple-users-select__status"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
        />
      </div>

      <div
        v-else-if="!filteredUsers.length"
        class="d-flex justify-center align-center multiple-users-select__status"
      >
        Users not found
      </div>

      <div
        v-else
        class="multiple-users-select__list"
      >
        <VListItem
          v-for="user in filteredUsers"
          :key="user.id"
          :subtitle="user.email"
          :disabled="isUserDisabled(user.id)"
          @click="toggleUserSelection(user)"
        >
          <template #prepend>
            <VCheckboxBtn :model-value="isUserSelected(user.id)" />
          </template>
          <VListItemTitle>
            {{ getUserTitle(user) }}
          </VListItemTitle>
        </VListItem>
      </div>

      <div
        v-if="isNextUsersPageExist && !loadAll"
        class="pa-4 pt-2"
      >
        <VBtn
          color="default"
          block
          :disabled="!!isLoading"
          @click="onLoadMoreUsers"
        >
          Load more...
          <VProgressCircular
            v-if="!!isLoading"
            class="margin-left-5"
            size="22"
            indeterminate
            color="white"
          />
        </VBtn>
      </div>
    </VCard>
  </VMenu>

  <VAutocomplete
    v-else
    v-bind="attrs"
    :model-value="modelValue"
    :items="[...filteredUsers]"
    return-object
    :item-title="i => i.display_name ? i.display_name : `${i.first_name} ${i.last_name}`"
    item-value="id"
    :label="label"
    :loading="isLoading"
    density="compact"
    :no-filter="!selectOptions"
    multiple
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusUserSelect"
    @update:search="handleAutocompleteSearch"
  >
    <template #prepend-item>
      <VListItem
        v-if="isSelectAllVisible && filteredUsers?.length > 1"
        @click="toggleSelectAll"
        class="v-list-item--select-all"
      >
        <template #prepend>
          <VCheckboxBtn
            :model-value="allFilteredSelected"
            :indeterminate="someFilteredSelected"
          />
        </template>
        <VListItemTitle>
          {{ allFilteredSelected || someFilteredSelected ? 'Deselect' : 'Select' }} All
          ({{ filteredUsers.length }} found)
        </VListItemTitle>
      </VListItem>
      <VDivider v-if="isSelectAllVisible && filteredUsers?.length > 1" />
    </template>
    <template
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :subtitle="item?.raw?.email"
        :disabled="!!disabledUsers ? disabledUsers.includes(item?.value) : false"
      >
        <template #prepend="{ isActive }">
          <VCheckboxBtn :model-value="isActive" />
        </template>
      </VListItem>
    </template>
    <template #selection="{ item, index }">
      <div class="d-flex align-center text-truncate">
        <v-chip v-if="index < 1">
          <span
            class="text-truncate"
            :title="item?.title"
          >
            {{ item?.title }}
          </span>
        </v-chip>
        <span
          v-if="index === 1"
          class="text-grey text-caption align-self-center"
        >
          (+{{ modelValue?.length - 1 }} others)
        </span>
      </div>
    </template>
    <template #no-data>
      <div
        v-if="!!isLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Users not found
      </div>
    </template>
    <template v-if="isNextUsersPageExist && !loadAll" #append-item>
      <VBtn
        color="default"
        block
        :disabled="!!isLoading"
        @click="onLoadMoreUsers"
      >
        Load more...
        <VProgressCircular
          v-if="!!isLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>

<style lang="scss">
.multiple-users-select__menu {
  min-width: 200px;
}

.multiple-users-select__status {
  min-height: 88px;
  padding: 16px;
}

.multiple-users-select__list {
  max-height: 320px;
  overflow: auto;
}

.multiple-users-select__selection {
  min-width: 0;
  max-width: min(50vw, 270px);
}

.multiple-users-select__chip {
  min-width: 0;
  max-width: min(50vw, 270px);
}

.multiple-users-select__chip .v-chip__content {
  min-width: 0;
  overflow: hidden;
}

.multiple-users-select__chip-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiple-users-select__chip-menu {
  max-width: min(90vw, 420px);
  overflow-wrap: anywhere;
  white-space: normal;
}

.multiple-users-select__more {
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .multiple-users-select__selection,
  .multiple-users-select__chip {
    max-width: min(38vw, 220px);
  }
}
</style>
